import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, Tabs, Tooltip, Typography, alpha, useTheme, Theme,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import feeConcessionService, { ConcessionApprovalStatus, FeeConcession } from '../../../services/feeConcessionService';
import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import { ApplicationConcessionRequest, ConcessionRequestStatus } from '../../../types/admissions';
import { useAuth } from '../../../context/AuthContext';
import { useApprovalRule } from '../../../hooks/useApprovalRule';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { useSubscription } from '../../../context/SubscriptionContext';
import { useCurrencyFormat } from '../../../hooks/useCurrencyFormat';
import { useTimezone } from '../../../hooks/useTimezone';
import { concessionAmountLabel } from '../../../utils/admissionFees';
import { concessionTypeLabel } from '../../admissions/dialogs/ConcessionRequestDialog';
import EmptyState from '../../common/EmptyState';
import ReasonDialog from '../../common/ReasonDialog';
import ConfirmationDialog, { ConfirmFacts } from '../../common/ConfirmationDialog';
import TableRowSkeletons from '../../common/TableRowSkeletons';

/** The same three tabs, as admission requests name their decisions. */
const ADMISSION_STATUS: Record<ConcessionApprovalStatus, ConcessionRequestStatus> = { PENDING: 'pending', APPROVED: 'approved', REJECTED: 'rejected' };

const TABS: Array<{ value: ConcessionApprovalStatus; label: string }> = [
  { value: 'PENDING', label: 'Waiting for approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Sent back' },
];

const headSx = (theme: Theme) => ({ bgcolor: alpha(theme.palette.text.primary, 0.02) });

/**
 * Concessions that need a second person: recurring fee concessions (a
 * percentage, or a flat amount above the school's limit) and the admission
 * concession requests waiting for a decision.
 */
const ConcessionApprovalsPanel: React.FC = () => {
  const theme = useTheme();
  const { decodedToken } = useAuth();
  const { ownBlocked } = useApprovalRule();
  const { canWrite } = useModulePermissions();
  const { hasFeature } = useSubscription();
  const { formatDate } = useTimezone();
  const money = useCurrencyFormat();
  const { enqueueSnackbar } = useSnackbar();
  const mayDecide = canWrite('fee_concessions');
  const seesAdmissions = hasFeature('admissions') && canWrite('admissions_concessions_approve');

  const [status, setStatus] = useState<ConcessionApprovalStatus>('PENDING');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState<{ items: FeeConcession[]; total: number } | null>(null);
  const [admissions, setAdmissions] = useState<{ items: ApplicationConcessionRequest[]; total: number } | null>(null);
  const [admissionPage, setAdmissionPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<FeeConcession | null>(null);
  const [deciding, setDeciding] = useState<{ row: ApplicationConcessionRequest; decision: 'approved' | 'rejected' } | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await feeConcessionService.listConcessions({ approvalStatus: status, page: page + 1, limit });
      setData({ items: r.concessions, total: r.pagination.totalItems });
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load concessions.');
    }
  }, [status, page, limit]);

  const loadAdmissions = useCallback(async () => {
    if (!seesAdmissions) return;
    try {
      const r = await admissionApplicationService.listConcessionRequests({ status: ADMISSION_STATUS[status], page: admissionPage + 1, limit: 10 });
      setAdmissions({ items: r.items, total: r.pagination.totalItems });
    } catch (e: any) {
      setError(e?.message || 'Could not load admission concession requests.');
    }
  }, [seesAdmissions, status, admissionPage]);

  useEffect(() => { setData(null); load(); }, [load]);
  useEffect(() => { loadAdmissions(); }, [loadAdmissions]);

  const approve = async (c: FeeConcession) => {
    setBusyId(c._id);
    try {
      await feeConcessionService.approveConcession(c._id);
      enqueueSnackbar('Concession approved — it applies to challans generated from now', { variant: 'success' });
      await load();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not approve this.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const decideAdmission = async () => {
    if (!deciding) return;
    setBusyId('admission');
    try {
      await admissionApplicationService.decideConcessionRequest(deciding.row.applicationId, deciding.row.index, deciding.decision);
      enqueueSnackbar(deciding.decision === 'approved' ? 'Concession approved' : 'Concession rejected', { variant: 'success' });
      setDeciding(null);
      await loadAdmissions();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not decide this request.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const items = data?.items ?? [];
  const pending = status === 'PENDING';

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs value={status} onChange={(_, v) => { setStatus(v); setPage(0); setAdmissionPage(0); }} sx={{ px: 1, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', minHeight: 44 } }}>
          {TABS.map(t => <Tab key={t.value} value={t.value} label={t.label} />)}
        </Tabs>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={headSx(theme)}>
                <TableCell>Student</TableCell>
                <TableCell>Concession</TableCell>
                <TableCell>From</TableCell>
                <TableCell>{pending ? 'Raised by' : 'Decided'}</TableCell>
                {pending && <TableCell align="right" sx={{ width: 220 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {!data ? <TableRowSkeletons columns={pending ? 5 : 4} rows={3} /> : items.map(c => {
                const own = ownBlocked(c.createdBy === decodedToken?.userId);
                return (
                  <TableRow key={c._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{c.studentName ?? '—'}</Typography>
                      {c.grNumber && <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled' }}>{c.grNumber}</Typography>}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 340 }}>
                      <Typography variant="body2">
                        {c.label} · <Box component="span" sx={{ fontWeight: 700 }}>{c.mode === 'percent' ? `${c.amount}%` : money(c.amount)}</Box>
                        <Typography component="span" variant="caption" color="text.secondary"> on {c.appliesTo === 'all' ? 'every fee' : (c.categoryNames ?? []).join(', ')}</Typography>
                      </Typography>
                      {c.reason && <Typography variant="caption" color="text.secondary" display="block" noWrap title={c.reason}>{c.reason}</Typography>}
                      {c.rejectionReason && <Typography variant="caption" color="warning.dark" display="block">Sent back: {c.rejectionReason}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(c.validFrom)}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.validTo ? `to ${formatDate(c.validTo)}` : 'until revoked'}</Typography>
                    </TableCell>
                    <TableCell>
                      {pending && <Typography variant="body2">{c.createdByName ?? '—'}</Typography>}
                      <Typography variant={pending ? 'caption' : 'body2'} color={pending ? 'text.secondary' : undefined}>{formatDate(pending ? c.createdAt : c.decidedAt ?? c.createdAt)}</Typography>
                    </TableCell>
                    {pending && (
                      <TableCell align="right">
                        {mayDecide && (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" color="inherit" onClick={() => setRejecting(c)} disabled={busyId === c._id}>Send back</Button>
                            <Tooltip title={own ? 'You raised this — someone else must approve it.' : ''}>
                              <span>
                                <Button size="small" variant="contained" disableElevation onClick={() => approve(c)} disabled={own || busyId === c._id}>
                                  {busyId === c._id ? 'Approving…' : 'Approve'}
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {data && items.length === 0 && (
          <Box sx={{ p: 3 }}>
            <EmptyState icon={<CheckCircleOutline />} title={pending ? 'No fee concessions waiting' : 'Nothing here yet'}
              description="Percentage concessions, and flat ones above the school's limit, wait here for a second person." dense />
          </Box>
        )}
        <TablePagination component="div" count={data?.total ?? 0} page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit} onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} />
      </Paper>

      {seesAdmissions && admissions && admissions.total > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={700}>Admission concession requests</Typography>
            <Typography variant="caption" color="text.secondary">Raised on applications; approved ones come off the admission challan or start with enrollment.</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={headSx(theme)}>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Concession</TableCell>
                  <TableCell>{pending ? 'Raised by' : 'Decided by'}</TableCell>
                  {pending && <TableCell align="right" sx={{ width: 220 }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {admissions.items.map(row => {
                  const r = row.request;
                  const requester = typeof r.requestedBy === 'string' ? r.requestedBy : r.requestedBy?._id;
                  const own = ownBlocked(requester === decodedToken?.userId);
                  const key = `${row.applicationId}-${row.index}`;
                  return (
                    <TableRow key={key} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                        {row.fatherName && <Typography variant="caption" color="text.secondary">{row.fatherName}</Typography>}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 340 }}>
                        <Typography variant="body2">
                          {concessionTypeLabel(r.type)} · <Box component="span" sx={{ fontWeight: 700 }}>{concessionAmountLabel(r)}</Box>
                          <Typography component="span" variant="caption" color="text.secondary"> {r.carryToFees ? 'monthly, from enrollment' : 'off the admission fee'}</Typography>
                        </Typography>
                        {r.reason && <Typography variant="caption" color="text.secondary" display="block" noWrap title={r.reason}>{r.reason}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{(pending ? row.requestedByName : row.decidedByName) ?? '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDate(pending ? r.requestedAt : r.approvedAt ?? r.requestedAt)}</Typography>
                      </TableCell>
                      {pending && <TableCell align="right">
                        <Tooltip title={own ? 'You requested this — someone else must decide it.' : ''}>
                          <span>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button size="small" color="inherit" disabled={own} onClick={() => setDeciding({ row, decision: 'rejected' })}>Reject</Button>
                              <Button size="small" variant="contained" disableElevation disabled={own} onClick={() => setDeciding({ row, decision: 'approved' })}>Approve</Button>
                            </Stack>
                          </span>
                        </Tooltip>
                      </TableCell>}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {admissions.total > 10 && (
            <TablePagination component="div" count={admissions.total} page={admissionPage} onPageChange={(_, p) => setAdmissionPage(p)}
              rowsPerPage={10} rowsPerPageOptions={[10]} />
          )}
        </Paper>
      )}

      <ReasonDialog
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title="Send this concession back?"
        consequence="It never applies. Whoever raised it sees your reason."
        facts={rejecting ? [
          { label: 'Student', value: rejecting.studentName ?? '' },
          { label: 'Concession', value: `${rejecting.label} · ${rejecting.mode === 'percent' ? `${rejecting.amount}%` : money(rejecting.amount)}` },
        ] : []}
        confirmText="Send back"
        confirmColor="warning"
        reasonPlaceholder="What needs to change?"
        onConfirm={async reason => {
          await feeConcessionService.rejectConcession(rejecting!._id, reason);
          enqueueSnackbar('Sent back', { variant: 'success' });
          await load();
        }}
      />

      <ConfirmationDialog
        open={!!deciding}
        onClose={() => setDeciding(null)}
        onConfirm={decideAdmission}
        loading={busyId === 'admission'}
        title={deciding?.decision === 'approved' ? 'Approve this concession?' : 'Reject this concession?'}
        message={deciding?.decision === 'approved'
          ? (deciding.row.request.carryToFees ? 'It starts with the student\'s monthly fees once enrolled.' : 'It comes off the admission challan straight away.')
          : 'It will not apply. The application keeps the record.'}
        confirmText={deciding?.decision === 'approved' ? 'Approve' : 'Reject'}
        confirmColor={deciding?.decision === 'approved' ? 'primary' : 'warning'}
      >
        {deciding && (
          <ConfirmFacts items={[
            { label: 'Applicant', value: deciding.row.name },
            { label: 'Concession', value: `${concessionTypeLabel(deciding.row.request.type)} · ${concessionAmountLabel(deciding.row.request)}` },
          ]} />
        )}
      </ConfirmationDialog>
    </Stack>
  );
};

export default ConcessionApprovalsPanel;
